import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Video, Trash2, Save, Loader2, Play, Upload } from "lucide-react";
import adminApi, { apiError } from "@/lib/adminApi";

const GALLERIES = [
  { key: "stage_payet", label: "Stage Payet" },
  { key: "stage_pro_semi_pro", label: "Stage Pro / Semi-Pro" },
  { key: "friendly_matches", label: "Matchs amicaux" },
  { key: "qui_sommes_nous", label: "Qui sommes-nous" },
];

const CHUNK_SIZE = 4 * 1024 * 1024; // 4 Mo par morceau

const inputClass =
  "bg-[#050505] border border-white/10 text-white px-3 py-2 text-sm focus:border-gold outline-none w-full";

const isMp4 = (url = "") => /\.mp4(\?|$)/i.test(url);

const AdminGaleries = () => {
  const [tab, setTab] = useState("stage_payet");
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(null); // null = inactif, 0-100 = envoi en cours
  const [videoPhase, setVideoPhase] = useState(""); // "envoi" | "compression"
  const [videoForm, setVideoForm] = useState({ url: "", title: "", display_order: 0 });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileRef = useRef(null);
  const videoFileRef = useRef(null);

  const busy = uploading || videoProgress !== null;

  const load = () =>
    adminApi.get("/media", { params: { gallery_type: tab } }).then(({ data }) => setMedia(data)).catch(() => {});
  useEffect(() => { load(); }, [tab]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("gallery_type", tab);
      fd.append("title", file.name.replace(/\.[^.]+$/, ""));
      fd.append("display_order", String(media.length + 1));
      fd.append("file", file);
      await adminApi.post("/media/photo", fd);
      toast.success("Photo ajoutée à la galerie.");
      load();
    } catch (err) {
      toast.error(apiError(err, "Upload impossible."));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const uploadMp4 = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.mp4$/i.test(file.name)) {
      toast.error("Format accepté : MP4 uniquement.");
      if (videoFileRef.current) videoFileRef.current.value = "";
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      toast.error("Vidéo trop volumineuse (200 Mo maximum).");
      if (videoFileRef.current) videoFileRef.current.value = "";
      return;
    }
    setVideoProgress(0);
    setVideoPhase("envoi");
    try {
      const { data: init } = await adminApi.post("/media/video/upload/init", {
        gallery_type: tab,
        filename: file.name,
      });
      const chunks = Math.ceil(file.size / CHUNK_SIZE);
      for (let i = 0; i < chunks; i++) {
        const fd = new FormData();
        fd.append("upload_id", init.upload_id);
        fd.append("chunk_index", String(i));
        fd.append("file", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE), `chunk-${i}.mp4`);
        await adminApi.post("/media/video/upload/chunk", fd, { timeout: 120000 });
        setVideoProgress(Math.round(((i + 1) / (chunks + 1)) * 100));
      }
      setVideoPhase("compression");
      setVideoProgress(99);
      await adminApi.post(
        "/media/video/upload/complete",
        {
          upload_id: init.upload_id,
          title: file.name.replace(/\.[^.]+$/, ""),
          display_order: media.length + 1,
        },
        { timeout: 300000 }
      );
      setVideoProgress(100);
      toast.success("Vidéo MP4 compressée et ajoutée à la galerie.");
      load();
    } catch (err) {
      toast.error(apiError(err, "Envoi de la vidéo impossible."));
    } finally {
      setVideoProgress(null);
      setVideoPhase("");
      if (videoFileRef.current) videoFileRef.current.value = "";
    }
  };

  const addVideo = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      await adminApi.post("/media/video", { ...videoForm, gallery_type: tab });
      toast.success("Vidéo ajoutée à la galerie.");
      setVideoForm({ url: "", title: "", display_order: 0 });
      load();
    } catch (err) {
      toast.error(apiError(err, "Ajout impossible."));
    } finally {
      setUploading(false);
    }
  };

  const saveItem = async (item) => {
    try {
      await adminApi.put(`/media/${item.id}`, { title: item.title, display_order: item.display_order });
      toast.success("Média mis à jour.");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (item) => {
    try {
      await adminApi.delete(`/media/${item.id}`);
      toast.success("Média supprimé de la galerie publique.");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const updateLocal = (id, key, value) =>
    setMedia(media.map((m) => (m.id === id ? { ...m, [key]: value } : m)));

  return (
    <div data-testid="admin-galeries" className="space-y-6">
      <h1 className="font-display uppercase text-4xl tracking-wide">Galeries</h1>

      <div className="flex flex-wrap gap-2">
        {GALLERIES.map((g) => (
          <button
            key={g.key}
            data-testid={`gallery-tab-${g.key}`}
            onClick={() => setTab(g.key)}
            className={`px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] border transition-colors ${
              tab === g.key ? "bg-gold text-noir border-gold" : "border-white/15 text-white/50 hover:text-white"
            }`}
          >
            {g.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-noir-surface border border-noir-border p-5">
          <h2 className="font-display uppercase text-lg tracking-wide text-gold mb-3">+ Ajouter une photo</h2>
          <p className="text-xs text-white/40 mb-3">JPG, JPEG, PNG ou WEBP — 8 Mo maximum.</p>
          <input ref={fileRef} data-testid="photo-upload-input" id="gallery-photo-file" type="file" accept=".jpg,.jpeg,.png,.webp" onChange={uploadPhoto} className="hidden" />
          <label
            htmlFor="gallery-photo-file"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-rouge text-white text-xs font-bold uppercase tracking-wider rounded-full hover:bg-rouge-hover transition-colors cursor-pointer"
          >
            <ImagePlus size={14} /> Choisir une photo
          </label>
          {uploading && <Loader2 size={16} className="animate-spin text-gold mt-3" />}
        </div>

        <div className="bg-noir-surface border border-noir-border p-5">
          <h2 className="font-display uppercase text-lg tracking-wide text-gold mb-3">+ Ajouter une vidéo MP4</h2>
          <p className="text-xs text-white/40 mb-3">
            Fichier .mp4 — 200 Mo maximum. La vidéo est compressée automatiquement pour un chargement rapide sur mobile. Ne fermez pas la page pendant l'envoi.
          </p>
          <input ref={videoFileRef} data-testid="video-upload-input" id="gallery-video-file" type="file" accept=".mp4,video/mp4" onChange={uploadMp4} className="hidden" />
          <label
            htmlFor="gallery-video-file"
            className={`inline-flex items-center gap-2 px-5 py-2.5 bg-rouge text-white text-xs font-bold uppercase tracking-wider rounded-full hover:bg-rouge-hover transition-colors cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}
          >
            <Upload size={14} /> Choisir une vidéo MP4
          </label>
          {videoProgress !== null && (
            <div data-testid="video-upload-progress" className="mt-3">
              <div className="h-1.5 bg-white/10 w-full overflow-hidden">
                <div className="h-full bg-gold transition-[width] duration-300" style={{ width: `${videoProgress}%` }} />
              </div>
              <p className="text-xs text-gold mt-1.5 font-bold">
                {videoPhase === "compression" ? "Compression en cours… (vidéo optimisée pour le web)" : `${videoProgress}% — envoi en cours…`}
              </p>
            </div>
          )}
        </div>

        <form onSubmit={addVideo} className="bg-noir-surface border border-noir-border p-5 space-y-3">
          <h2 className="font-display uppercase text-lg tracking-wide text-gold">+ Vidéo YouTube / Vimeo</h2>
          <p className="text-xs text-white/40">Collez l'URL d'une vidéo YouTube ou Vimeo (https).</p>
          <input data-testid="video-url-input" value={videoForm.url} onChange={(e) => setVideoForm({ ...videoForm, url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" className={inputClass} required />
          <input data-testid="video-title-input" value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} placeholder="Titre de la vidéo" className={inputClass} />
          <button data-testid="video-add-button" disabled={busy} className="inline-flex items-center gap-2 px-5 py-2.5 bg-rouge text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover disabled:opacity-50">
            <Video size={14} /> Ajouter la vidéo
          </button>
        </form>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
        {media.map((m) => (
          <div key={m.id} data-testid={`media-item-${m.id}`} className="bg-noir-surface border border-noir-border overflow-hidden">
            <div className="relative aspect-video bg-black">
              {m.media_type === "image" ? (
                <img src={m.url} alt={m.title} loading="lazy" className="w-full h-full object-cover" />
              ) : isMp4(m.url) ? (
                <video data-testid={`media-preview-${m.id}`} src={m.url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Play size={28} className="text-gold" />
                </div>
              )}
              <span className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-[0.15em] bg-noir/80 border border-gold/40 text-gold px-2 py-0.5">
                {m.media_type === "image" ? "Photo" : "Vidéo"}
              </span>
            </div>
            <div className="p-3 space-y-2">
              <input value={m.title} onChange={(e) => updateLocal(m.id, "title", e.target.value)} data-testid={`media-title-${m.id}`} className={inputClass} placeholder="Titre" />
              <div className="flex items-center gap-2">
                <input type="number" value={m.display_order} onChange={(e) => updateLocal(m.id, "display_order", Number(e.target.value))} data-testid={`media-order-${m.id}`} className={`${inputClass} w-20`} title="Ordre d'affichage" />
                <button onClick={() => saveItem(m)} data-testid={`media-save-${m.id}`} className="w-8 h-8 flex items-center justify-center border border-white/15 text-white/60 hover:border-gold hover:text-gold transition-colors" title="Enregistrer">
                  <Save size={13} />
                </button>
                <button onClick={() => setConfirmDelete(m)} data-testid={`media-delete-${m.id}`} className="w-8 h-8 flex items-center justify-center border border-white/15 text-white/60 hover:border-rouge hover:text-rouge-hover transition-colors" title="Supprimer">
                  <Trash2 size={13} />
                </button>
              </div>
              <p className="text-[10px] text-white/30">Ajouté le {m.created_at ? new Date(m.created_at).toLocaleDateString("fr-FR") : "—"}</p>
            </div>
          </div>
        ))}
        {media.length === 0 && <p className="text-white/40 text-sm col-span-full">Aucun média dans cette galerie.</p>}
      </div>

      {confirmDelete && (
        <div data-testid="media-delete-confirm" className="fixed inset-0 z-50 bg-noir/90 backdrop-blur flex items-center justify-center p-6">
          <div className="bg-noir-surface border border-rouge/40 p-8 max-w-md w-full">
            <h2 className="font-display uppercase text-2xl tracking-wide text-rouge-hover">Supprimer ce média ?</h2>
            <p className="mt-3 text-white/60 text-sm">
              Voulez-vous vraiment supprimer {confirmDelete.media_type === "image" ? "cette photo" : "cette vidéo"} ?
              Elle disparaîtra immédiatement de la galerie publique.
            </p>
            <div className="mt-6 flex gap-3">
              <button data-testid="media-delete-yes" onClick={() => remove(confirmDelete)} className="px-6 py-3 bg-rouge text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover">
                Supprimer
              </button>
              <button data-testid="media-delete-no" onClick={() => setConfirmDelete(null)} className="px-6 py-3 border border-white/20 text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:border-white">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGaleries;
